import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, RefreshCw, ExternalLink, PhoneCall, PhoneOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { inr } from './IsaKpis';

const crmLeadUrl = id => `https://crm.zoho.com/crm/org690252559/tab/Leads/${id}`;
const fmt = v => (v ? new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const idleCls = d => (d == null ? 'text-slate-400' : d > 90 ? 'text-rose-600 font-bold' : d > 30 ? 'text-orange-600 font-semibold' : d > 7 ? 'text-amber-600' : 'text-slate-600');

const Stat = ({ label, value, sub, cls = 'text-slate-900', testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className={`text-xl font-bold mt-0.5 ${cls}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </Card>
);

const Buckets = ({ buckets, total }) => (
  <Card className="p-4" data-testid="isa-backlog-buckets">
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Time since last touched</div>
    {buckets.map((b, i) => (
      <div key={b.label} className="flex items-center gap-2 text-xs mb-1">
        <span className="w-24 text-slate-600">{b.label}</span>
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${['bg-slate-400', 'bg-amber-400', 'bg-orange-500', 'bg-rose-600'][i]}`} style={{ width: `${total ? (b.count * 100) / total : 0}%` }} />
        </div>
        <span className="w-8 text-right font-semibold text-slate-800">{b.count}</span>
      </div>
    ))}
  </Card>
);

export const IsaBacklogTab = () => {
  const [kind, setKind] = useState('callback');
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    return api.get('/inside-sales-audit/backlog', { params: { kind } }).then(r => setD(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not read the CRM')).finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => { setD(null); setAll(false); load(); }, [load]);

  const rows = useMemo(() => (d?.leads || []).filter(x => !q || `${x.name} ${x.person} ${x.source}`.toLowerCase().includes(q.toLowerCase())), [d, q]);
  const shown = all ? rows : rows.slice(0, 100);
  const label = kind === 'callback' ? 'Call Back' : 'Unable to Reach';

  return (
    <div className="space-y-4" data-testid="isa-backlog-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="isa-backlog-kind">
          {[['callback', 'Call Back', PhoneCall], ['utr', 'Unable to Reach', PhoneOff]].map(([v, l, Icon]) => (
            <button key={v} onClick={() => setKind(v)} data-testid={`isa-backlog-kind-${v}`}
              className={`text-xs px-3 py-1 rounded inline-flex items-center gap-1.5 transition-colors ${kind === v ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Icon className="w-3.5 h-3.5" />{l}</button>
          ))}
        </div>
        <span className="text-[11px] text-slate-500">{d ? `Live from Zoho CRM · ${d.at_ist} IST` : ''}</span>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="ml-auto h-8" data-testid="isa-backlog-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}Refresh</Button>
      </div>
      {!d && <div className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Reading {label} leads from the CRM…</div>}
      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat label={`${label} leads now`} value={d.total} sub={`limit ${d.limit}`} cls={d.total > d.limit ? 'text-rose-600' : 'text-emerald-600'} testid="isa-backlog-total" />
            <Stat label="Penalty if the day ended now" value={inr(-d.penalty_if_now)} sub={d.rule} cls={d.penalty_if_now ? 'text-rose-600' : 'text-slate-400'} testid="isa-backlog-penalty" />
            <Stat label="Clear to get under the limit" value={d.to_clear} sub={d.to_clear ? `clearing ${d.to_clear} saves ${inr(d.penalty_if_now)} a day` : 'already within the limit'} testid="isa-backlog-to-clear" />
            <Stat label="By person" value={Object.entries(d.by_person).map(([n, c]) => `${n} ${c}`).join(' · ')} sub={kind === 'callback' ? 'counted by CRM lead owner' : 'credited by Inbound Sales Person Emp ID'} testid="isa-backlog-by-person" />
            <Buckets buckets={d.buckets} total={d.total} />
          </div>
          <Card className="overflow-x-auto" data-testid="isa-backlog-table">
            <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Oldest first — clear these first</h3>
                <p className="text-[11px] text-slate-500">Sorted by how long since anyone last updated the lead in the CRM.</p>
              </div>
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search lead, person, source…" className="h-8 w-60 text-xs" data-testid="isa-backlog-search" />
            </div>
            <table className="w-full text-xs min-w-[760px]">
              <thead className="border-y border-slate-100 bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-400">
                <tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Lead</th><th className="px-3 py-2 text-left">Person</th><th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Last touched</th><th className="px-3 py-2 text-right">Days idle</th><th className="px-3 py-2 text-left">Created</th><th /></tr>
              </thead>
              <tbody>
                {shown.map((x, i) => (
                  <tr key={x.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`isa-backlog-row-${x.id}`}>
                    <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{x.name || x.id}</td>
                    <td className="px-3 py-2">{x.person}</td>
                    <td className="px-3 py-2 text-slate-500">{x.source || '—'}</td>
                    <td className="px-3 py-2">{fmt(x.last_touched)}</td>
                    <td className={`px-3 py-2 text-right ${idleCls(x.days_idle)}`}>{x.days_idle ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-500">{fmt(x.created)}</td>
                    <td className="px-3 py-2 text-right"><a href={crmLeadUrl(x.id)} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center gap-1">Open in CRM <ExternalLink className="w-3 h-3" /></a></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No {label} leads.</td></tr>}
              </tbody>
            </table>
            {rows.length > 100 && !all && (
              <div className="p-3 text-center"><Button size="sm" variant="ghost" onClick={() => setAll(true)} data-testid="isa-backlog-show-all">Show all {rows.length}</Button></div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
